import MobileRecordCard from './MobileRecordCard';
import { formatTRY } from '@/lib/currencyFormat';
import Badge from '@/components/base/Badge';
import { paymentStatus, deliveryStatus } from '@/lib/statusDefs';

interface CustomerOverviewTabProps {
  customer: any;
  sales: any[];
  payments: any[];
  onEditNotes: () => void;
  onNavigate: (path: string) => void;
}

export default function CustomerOverviewTab({ customer, sales, payments, onEditNotes, onNavigate }: CustomerOverviewTabProps) {
  const recentSales = (sales || []).slice(0, 5);
  const recentPayments = (payments || []).slice(0, 5);

  return (
    <div className="p-4 lg:p-5 space-y-5">
      {/* Customer Note */}
      <div>
        <h3 className="text-sm font-semibold text-foreground-800 mb-3">Müşteri Notu</h3>
        {customer.notes ? (
          <div className="bg-amber-50/60 border border-amber-200/70 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <span className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                <i className="ri-sticky-note-line text-amber-600 text-sm"></i>
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-foreground-700 whitespace-pre-wrap break-words">{customer.notes}</p>
              </div>
              <button
                onClick={onEditNotes}
                className="w-7 h-7 rounded-lg hover:bg-amber-100 flex items-center justify-center cursor-pointer flex-shrink-0"
                title="Notu Düzenle"
              >
                <i className="ri-edit-line text-amber-600 text-sm"></i>
              </button>
            </div>
          </div>
        ) : (
          <div className="bg-background-100/50 border border-dashed border-background-200 rounded-xl p-5 text-center">
            <div className="w-10 h-10 rounded-xl bg-background-100 flex items-center justify-center mx-auto mb-2">
              <i className="ri-sticky-note-line text-lg text-foreground-300"></i>
            </div>
            <p className="text-sm text-foreground-500 mb-2">Henüz müşteri notu eklenmemiş.</p>
            <button
              onClick={onEditNotes}
              className="btn-outline text-sm cursor-pointer whitespace-nowrap inline-flex items-center gap-1.5"
            >
              <i className="ri-add-line"></i> Not Ekle
            </button>
          </div>
        )}
      </div>

      {/* Recent Sales */}
      <div>
        <h3 className="text-sm font-semibold text-foreground-800 mb-3">Son Satışlar</h3>
        {recentSales.length > 0 ? (
          <div>
            {/* Desktop table */}
            <div className="hidden lg:block overflow-x-auto">
              <table className="w-full min-w-[600px]">
                <thead><tr className="border-b border-background-100"><th className="table-header">Satış No</th><th className="table-header">Kategori</th><th className="table-header">Tutar</th><th className="table-header">Kalan</th><th className="table-header">Ödeme</th><th className="table-header">Teslimat</th></tr></thead>
                <tbody>
                  {recentSales.map(sale => (
                    <tr key={sale.id} className="border-b border-background-100 hover:bg-background-50">
                      <td className="table-cell text-primary-600 font-medium">{sale.sale_no}</td>
                      <td className="table-cell text-foreground-500">{sale.product_category}</td>
                      <td className="table-cell font-medium">{formatTRY(sale.total_amount || 0)}</td>
                      <td className="table-cell">{(sale.remaining_amount || 0) > 0 ? <span className="text-red-500">{formatTRY(sale.remaining_amount)}</span> : <span className="text-emerald-600">—</span>}</td>
                      <td className="table-cell py-2"><Badge badgeClass={paymentStatus.badgeClass(sale.payment_status)} icon={paymentStatus.icon(sale.payment_status)}>{sale.payment_status}</Badge></td>
                      <td className="table-cell py-2"><Badge badgeClass={deliveryStatus.badgeClass(sale.delivery_status)} icon={deliveryStatus.icon(sale.delivery_status)}>{sale.delivery_status}</Badge></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Mobile cards */}
            <div className="lg:hidden space-y-2">
              {recentSales.map(sale => (
                <MobileRecordCard
                  key={sale.id}
                  title={sale.sale_no}
                  subtitle={sale.product_category}
                  rows={[
                    { label: 'Tutar', value: formatTRY(sale.total_amount || 0) },
                    { label: 'Kalan', value: (sale.remaining_amount || 0) > 0 ? formatTRY(sale.remaining_amount) : '—', highlight: (sale.remaining_amount || 0) > 0, color: 'red' },
                  ]}
                  badges={[
                    { text: sale.payment_status, className: paymentStatus.badgeClass(sale.payment_status), icon: paymentStatus.icon(sale.payment_status) },
                    { text: sale.delivery_status, className: deliveryStatus.badgeClass(sale.delivery_status), icon: deliveryStatus.icon(sale.delivery_status) },
                  ]}
                  actionLabel="Detay"
                  onAction={() => onNavigate(`/sales/${sale.id}`)}
                />
              ))}
            </div>
          </div>
        ) : (
          <p className="text-sm text-foreground-400 py-4">Henüz satış kaydı yok.</p>
        )}
      </div>

      {/* Recent Payments */}
      <div>
        <h3 className="text-sm font-semibold text-foreground-800 mb-3">Son Ödemeler</h3>
        {recentPayments.length > 0 ? (
          <div>
            {/* Desktop table */}
            <div className="hidden lg:block overflow-x-auto">
              <table className="w-full min-w-[500px]">
                <thead><tr className="border-b border-background-100"><th className="table-header">Tarih</th><th className="table-header">Satış</th><th className="table-header">Tutar</th><th className="table-header">Yöntem</th><th className="table-header">Makbuz</th></tr></thead>
                <tbody>
                  {recentPayments.map(payment => (
                    <tr key={payment.id} className="border-b border-background-100 hover:bg-background-50">
                      <td className="table-cell">{payment.payment_date ? new Date(payment.payment_date).toLocaleDateString('tr-TR') : ''}</td>
                      <td className="table-cell text-foreground-500">{payment.sale_no}</td>
                      <td className="table-cell font-medium text-emerald-600">{formatTRY(payment.amount || 0)}</td>
                      <td className="table-cell text-foreground-500">{payment.payment_method}</td>
                      <td className="table-cell text-foreground-400 text-xs">{payment.receipt_no}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Mobile cards */}
            <div className="lg:hidden space-y-2">
              {recentPayments.map(payment => (
                <MobileRecordCard
                  key={payment.id}
                  title={formatTRY(payment.amount || 0)}
                  subtitle={payment.payment_date ? new Date(payment.payment_date).toLocaleDateString('tr-TR') : ''}
                  rows={[
                    { label: 'Satış', value: payment.sale_no || '—' },
                    { label: 'Yöntem', value: payment.payment_method || '—' },
                    ...(payment.receipt_no ? [{ label: 'Makbuz', value: payment.receipt_no }] : []),
                  ]}
                />
              ))}
            </div>
          </div>
        ) : (
          <p className="text-sm text-foreground-400 py-4">Henüz ödeme kaydı yok.</p>
        )}
      </div>
    </div>
  );
}